import { Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'he', name: 'עברית', flag: '🇮🇱' },
];

// Map old blog URLs to new slug format
const blogSlugMap: Record<string, string> = {
  "what-is-ai-answering-service": "what-is-ai-answering-service",
  "ai-vs-human-agents-cost-comparison": "ai-vs-human-agents-cost",
  "reduce-customer-support-costs-with-ai": "reduce-customer-support-costs",
  "top-ways-businesses-use-ai-phone-support": "top-ways-ai-phone-support",
  "integrate-ai-customer-support-whatsapp": "integrate-ai-whatsapp",
  "ai-receptionist-for-small-business": "ai-receptionist-for-small-business",
  "ai-for-real-estate-agents": "ai-for-real-estate-agents",
  "ai-for-medical-clinics": "ai-for-medical-clinics",
  "ai-for-restaurants": "ai-for-restaurants",
  "best-ai-phone-agent-software": "best-ai-phone-agent-software",
  "how-to-set-up-ai-call-center": "how-to-set-up-ai-call-center",
  "ai-voice-agent-vs-chatbot": "ai-voice-agent-vs-chatbot",
  "future-of-ai-customer-service": "future-of-ai-customer-service",
  "chatgpt-mcp-phone-calls": "chatgpt-mcp-phone-calls",
  "ai-voice-cloning-phone-agent": "ai-voice-cloning-phone-agent",
  "openclaw-phone-calls": "openclaw-phone-calls",
};

export function LanguageSelector() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const currentLang = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = (langCode: string) => {
    // Check if we're on a blog post page (localized or legacy)
    const blogMatch = location.pathname.match(/^\/blog\/(?:([a-z]{2})\/)?(.+)$/);
    
    if (blogMatch) {
      const [, , slug] = blogMatch;
      // Get the normalized slug (handle both old and new URL formats)
      const normalizedSlug = blogSlugMap[slug] || slug;
      
      // Navigate to the localized version
      navigate(`/blog/${langCode}/${normalizedSlug}`);
    }
    
    // Always change the i18n language
    i18n.changeLanguage(langCode);
    localStorage.setItem("i18nextLng", langCode);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-10 px-3 gap-2">
          <Globe className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm">{currentLang.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={i18n.language === lang.code ? "bg-accent font-medium" : ""}
          >
            <span className="truncate leading-none">{lang.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
